import React, { useMemo, useState } from 'react';

import {
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Divider,
} from '@mui/material';
import _ from 'lodash';

import { ReactComponent as ChevronRightDarkBlue } from '../../../../common/assets/icons/chevron_right_dark_blue.svg';
import {
  CodeInvalidReason,
  SelectableCode,
} from '../../../../common/types/Codes';
import ModifierPopupMenu from '../../../screens/SearchResultsScreen/components/ModifierPopup/ModifierPopupMenu';
import MultiplierPopupMenu from '../../../screens/SearchResultsScreen/components/MultiplierPopupMenu';
import IOSSwitch from '../../../screens/TFactorScreen/IOSSwitch';
import { SelectUnitsButton } from '../components/SelectUnitsButton';

import { SelectModifiersButton } from './SelectModifiersButton';

type Props = {
  code: SelectableCode;
  selectCode: (code: SelectableCode) => void;
  selectMultiplier: (code: SelectableCode, multiplier: number) => void;
  selectModifiers: (code: SelectableCode, modifierCodes: string[]) => void;
  isICD10?: boolean;
};

const CodeComponent: React.FC<Props> = ({
  code,
  selectCode,
  selectMultiplier,
  selectModifiers,
  isICD10,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModifierPopupOpen, setIsModifierPopupOpen] = useState(false);
  const [anchorMultiplierEl, setAnchorMultiplierEl] =
    useState<HTMLButtonElement | null>(null);

  const handleRotate = () => setIsOpen(!isOpen);
  const rotate = isOpen ? 'rotate(90deg)' : 'rotate(0)';
  const multiplierIsOpen = Boolean(anchorMultiplierEl);

  const handleClickUnits = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorMultiplierEl(event.currentTarget);
  };

  const handleCloseUnits = () => {
    setAnchorMultiplierEl(null);
  };

  const handleSelectedUnit = (item: number) => {
    setAnchorMultiplierEl(null);
    selectMultiplier(code, item);
  };

  const onConfirmModifiers = (
    code: SelectableCode,
    modifierCodes: string[]
  ) => {
    setIsModifierPopupOpen(false);
    selectModifiers(code, modifierCodes);
  };

  const getInvalidComboString = (invalidCombos: CodeInvalidReason[]) => {
    return invalidCombos
      .map((reason) => `${reason.code}: ${reason.reason}`)
      .join('\n');
  };

  const codeState = useMemo(() => {
    if (code.selected && !_.isEmpty(code.invalidCombos)) return 'error';
    if (code.selected && code.isPrimaryCode) return 'error';
    if (code.selected) return 'good';

    return 'none';
  }, [code.selected, code.invalidCombos, code.isPrimaryCode]);

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        padding: '16px',
        borderRadius: '16px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: {
              xs: '4px',
              sm: '8px',
            },
            flexGrow: 1,
            overflow: 'hidden',
          }}
        >
          {!isICD10 && (
            <IconButton
              style={{
                transform: rotate,
                transition: 'all 0.25s ease-in-out',
                alignSelf: 'flex-start',
              }}
              onClick={() => handleRotate()}
            >
              <ChevronRightDarkBlue
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </IconButton>
          )}
          <Typography color='darkBlue' fontWeight={700} fontSize={16} noWrap>
            {code.code}
          </Typography>

          {!isOpen && (
            <Typography
              color='rgba(0, 33, 69, 0.7)'
              fontWeight={400}
              sx={{
                maxWidth: {
                  xs: '110px',
                  sm: '240px',
                  md: '300px',
                  lg: '348px',
                  xl: '600px',
                },
                fontSize: {
                  xs: '14px',
                  sm: '15px',
                },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {code.description}
            </Typography>
          )}
          <FormControlLabel
            style={{
              justifySelf: 'flex-end',
              marginLeft: 'auto',
              marginRight: 0,
            }}
            control={
              <IOSSwitch
                sx={{ m: 1 }}
                checked={code.selected}
                onChange={(event) => selectCode(code)}
              />
            }
            label=''
          />
          {/*{codeState === "error" ? (*/}
          {/*  <Tooltip*/}
          {/*    enterDelay={500}*/}
          {/*    disableFocusListener*/}
          {/*    disableTouchListener*/}
          {/*    sx={{*/}
          {/*      backgroundColor: "rgba(253, 240, 240, 1)",*/}
          {/*    }}*/}
          {/*    title={*/}
          {/*      <span style={{ whiteSpace: "pre-line" }}>*/}
          {/*        {getInvalidComboString(code.invalidCombos ?? [])}*/}
          {/*      </span>*/}
          {/*    }>*/}
          {/*    <FormControlLabel*/}
          {/*      style={{*/}
          {/*        justifySelf: "flex-end",*/}
          {/*        marginLeft: "auto",*/}
          {/*        marginRight: 0,*/}
          {/*      }}*/}
          {/*      control={*/}
          {/*        <IOSSwitch*/}
          {/*          sx={{ m: 1 }}*/}
          {/*          checked={code.selected}*/}
          {/*          onChange={(event) => selectCode(code)}*/}
          {/*        />*/}
          {/*      }*/}
          {/*      label=""*/}
          {/*    />*/}
          {/*  </Tooltip>*/}
          {/*) : (*/}
          {/*  <FormControlLabel*/}
          {/*    style={{*/}
          {/*      justifySelf: "flex-end",*/}
          {/*      marginLeft: "auto",*/}
          {/*      marginRight: 0,*/}
          {/*    }}*/}
          {/*    control={*/}
          {/*      <IOSSwitch*/}
          {/*        sx={{ m: 1 }}*/}
          {/*        checked={code.selected}*/}
          {/*        onChange={(event) => selectCode(code)}*/}
          {/*      />*/}
          {/*    }*/}
          {/*    label=""*/}
          {/*  />*/}
          {/*)}*/}
        </Box>
      </Box>

      {!isICD10 && isOpen && (
        <>
          <Divider style={{ marginTop: '12px', marginBottom: '16px' }} />
          <Typography
            color='rgba(0, 33, 69, 0.7)'
            fontWeight={400}
            fontSize={14}
          >
            {code.description}
          </Typography>
          <Divider style={{ marginTop: '16px', marginBottom: '8px' }} />
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: code.selected ? 'space-between' : 'flex-start',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: '6px',
              }}
            >
              <Typography
                fontWeight={700}
                fontSize={15}
                color='rgba(38, 96, 113, 1)'
              >
                {code.work_rvu}
              </Typography>
              <Typography fontSize={15} color='text.secondary'>
                Work RVU
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: '6px',
              }}
            >
              <Typography
                fontWeight={700}
                fontSize={15}
                color='rgba(38, 96, 113, 1)'
              >
                {code.facility_total}
              </Typography>
              <Typography fontSize={15} color='text.secondary'>
                Total RVU
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <SelectUnitsButton code={code} onClick={handleClickUnits} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SelectModifiersButton
                code={code}
                onClick={() => setIsModifierPopupOpen(true)}
              />
            </Box>
          </Box>
        </>
      )}

      <MultiplierPopupMenu
        handleSelectedItem={(item) => handleSelectedUnit(item)}
        anchorEl={anchorMultiplierEl}
        handleClose={handleCloseUnits}
        open={multiplierIsOpen}
        id={code.code}
      />
      <ModifierPopupMenu
        code={code}
        open={isModifierPopupOpen}
        onClose={() => setIsModifierPopupOpen(false)}
        onConfirm={(modifierCodes) => onConfirmModifiers(code, modifierCodes)}
      />
    </Box>
  );
};

export default CodeComponent;
